import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { PartnerAuthService } from './partner-auth.service';

/** Partner API surface the store-owner token is attached to (company-user scoped endpoints).
 *  Includes /api/auth so authenticated auth calls (e.g. verify-email/{id}) carry the token;
 *  the anonymous auth endpoints (login/validate/refresh/logout) simply ignore it. The optional `v1/`
 *  segment covers versioned module routes (HR is at /api/v1/hr/...). The Inventory master-data
 *  routes (item, itemcategory, unit, taxdefinition) and the store branding uploads
 *  (store-logo, store-banner) power the store page's Inventory tab + branding editor. */
// Note the notifications scope is deliberately narrowed to the company-user paths (device registration
// + the staff inbox) — NOT bare /api/notifications/, so a partner co-signed-in with a customer never
// clobbers the customer token on the customer inbox endpoints (/api/notifications/inbox…).
const PARTNER_API = /\/api\/(v1\/)?(sales|company|hr|auth|item|itemcategory|unit|taxdefinition|attributedefinition|store-logo|store-banner)(\/|$)|\/api\/notifications\/(device|staff)(\/|$)/i;

/** Auth calls that must never trigger the refresh/replay loop (they'd recurse). */
const NO_RETRY = /\/api\/auth\/(login|refresh-token|logout)(\/|$|\?)/i;

/** The refresh (or the token itself) is unusable — end the session and bounce to login. */
function endSession(auth: PartnerAuthService, router: Router, err?: HttpErrorResponse) {
  auth.clearSession();
  router.navigate(['/partner/login'], { queryParams: { reason: 'session-expired' } });
  return throwError(() => err ?? new HttpErrorResponse({ status: 401, statusText: 'Session expired' }));
}

/**
 * Attaches the partner (store-owner) token to company-user API calls (/api/sales, /api/Company,
 * /api/hr) when signed in. Mirrors the ERP auth interceptor: PROACTIVELY refreshes an already-expired
 * access token before sending, refreshes REACTIVELY on a server 401, shares one in-flight refresh
 * across concurrent requests, and on refresh failure clears the session and redirects to the partner
 * login (the reported "session never logs out" gap). Scoped by URL so the partner token never leaks
 * onto customer/rider/ordering calls. Registered LAST in app.config so it wins over the broad
 * customer interceptor for these endpoints.
 */
export const partnerAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(PartnerAuthService);
  const router = inject(Router);

  const token = auth.accessToken;
  if (!token || !PARTNER_API.test(req.url)) return next(req);

  const withToken = (t: string, r: HttpRequest<unknown> = req) =>
    r.clone({ setHeaders: { Authorization: `Bearer ${t}` } });
  const noRetry = NO_RETRY.test(req.url);

  const send = (n: HttpHandlerFn) =>
    n(withToken(auth.accessToken!)).pipe(
      catchError((err: HttpErrorResponse) => {
        // Reactive refresh on an unexpected server 401 (clock skew / server-side revocation).
        if (err.status === 401 && !noRetry) {
          return from(auth.refreshAccessToken()).pipe(
            switchMap((ok) => (ok ? next(withToken(auth.accessToken!)) : endSession(auth, router, err))),
          );
        }
        return throwError(() => err);
      }),
    );

  // Proactive: a locally-expired access token is refreshed before the request goes out, so a
  // returning owner with a live refresh token never even sees a failed call.
  if (!noRetry && auth.isTokenExpired()) {
    return from(auth.refreshAccessToken()).pipe(
      switchMap((ok) => (ok ? send(next) : endSession(auth, router))),
    );
  }

  return send(next);
};
